import axios from 'axios'
import store from '@/store'

const API_BASE = 'https://residencial-services.intrare.app'

/**
 * Convierte la respuesta de error de la API (DRF) en un mensaje legible
 * @param {Object} error - Error de axios
 * @returns {string} Mensaje de error para mostrar al usuario
 */
export function extractApiErrorMessage (error) {
  const data = error && error.response && error.response.data
  if (!data) return 'Ocurrió un error al conectar con el servidor'
  if (typeof data === 'string') return data
  if (data.detail) return data.detail

  const messages = []
  const flatten = (obj, prefix) => {
    Object.keys(obj).forEach(key => {
      const val = obj[key]
      const label = prefix ? `${prefix}.${key}` : key
      if (Array.isArray(val)) {
        val.forEach(v => messages.push(`${label}: ${v}`))
      } else if (val && typeof val === 'object') {
        flatten(val, label)
      } else {
        messages.push(`${label}: ${val}`)
      }
    })
  }
  flatten(data, '')
  return messages.join(' | ') || 'Ocurrió un error al procesar la solicitud'
}

export default {
  /**
   * Obtiene lista de áreas de un residencial
   * @param {string} housingUnitId - ID del residencial
   * @returns {Promise} Lista de áreas
   */
  async getAreas (housingUnitId) {
    const url = `${API_BASE}/employee-area/?housing_unit=${housingUnitId}`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching areas:', error)
      throw error
    }
  },

  /**
   * Crea una nueva área de trabajo
   * @param {string} housingUnitId - ID del residencial
   * @param {Object} areaData - Datos del área {name, description}
   * @returns {Promise} Área creada
   */
  async createArea (housingUnitId, areaData) {
    const url = `${API_BASE}/employee-area/`

    try {
      const response = await axios.post(url, {
        housing_unit: housingUnitId,
        name: areaData.name,
        description: areaData.description || ''
      }, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error creating area:', error)
      throw error
    }
  },

  /**
   * Actualiza un área de trabajo
   * @param {string} areaId - ID del área
   * @param {Object} areaData - Datos a actualizar {name, description}
   * @returns {Promise} Área actualizada
   */
  async updateArea (areaId, areaData) {
    const url = `${API_BASE}/employee-area/${areaId}/`

    try {
      const response = await axios.patch(url, {
        name: areaData.name,
        description: areaData.description || ''
      }, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error updating area:', error)
      throw error
    }
  },

  /**
   * Elimina un área de trabajo
   * @param {string} areaId - ID del área
   * @returns {Promise}
   */
  async deleteArea (areaId) {
    const url = `${API_BASE}/employee-area/${areaId}/`

    try {
      await axios.delete(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return true
    } catch (error) {
      console.error('Error deleting area:', error)
      throw error
    }
  },

  /**
   * Obtiene lista de empleados de un residencial
   * @param {string} housingUnitId - ID del residencial
   * @returns {Promise} Lista de empleados
   */
  async getEmployees (housingUnitId) {
    const url = `${API_BASE}/employee/?housing_unit=${housingUnitId}`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching employees:', error)
      throw error
    }
  },

  /**
   * Obtiene detalles de un empleado específico
   * @param {string} employeeId - ID del empleado
   * @returns {Promise} Detalles del empleado
   */
  async getEmployee (employeeId) {
    const url = `${API_BASE}/employee/${employeeId}/`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching employee:', error)
      throw error
    }
  },

  /**
   * Crea un nuevo empleado (usa FormData por los archivos de imagen)
   * @param {string} housingUnitId - ID del residencial
   * @param {Object} employeeData - Datos del empleado y usuario
   * @returns {Promise} Empleado creado
   */
  async createEmployee (housingUnitId, employeeData) {
    const url = `${API_BASE}/employee/`
    const formData = new FormData()

    formData.append('housing_unit', housingUnitId)
    formData.append('employee_number', employeeData.employee_number)
    formData.append('description', employeeData.description || '')
    formData.append('status', employeeData.status || 'active')
    if (employeeData.area) formData.append('area', employeeData.area)

    if (employeeData.user) {
      formData.append('user.email', employeeData.user.email)
      formData.append('user.username', employeeData.user.email)
      formData.append('user.first_name', employeeData.user.first_name)
      formData.append('user.last_name', employeeData.user.last_name)
      formData.append('user.cellNumber', employeeData.user.cellNumber)
      formData.append('user.password', employeeData.user.password)

      if (employeeData.user.idFront) formData.append('user.idFront', employeeData.user.idFront)
      if (employeeData.user.idBack) formData.append('user.idBack', employeeData.user.idBack)
      if (employeeData.user.avatar) formData.append('user.avatar', employeeData.user.avatar)
    }

    try {
      const response = await axios.post(url, formData, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error creating employee:', error)
      throw error
    }
  },

  /**
   * Actualiza un empleado (usa FormData por los archivos de imagen)
   * @param {string} employeeId - ID del empleado
   * @param {Object} employeeData - Datos a actualizar
   * @returns {Promise} Empleado actualizado
   */
  async updateEmployee (employeeId, employeeData) {
    const url = `${API_BASE}/employee/${employeeId}/`
    const formData = new FormData()

    if (employeeData.description !== undefined) formData.append('description', employeeData.description)
    if (employeeData.status !== undefined) formData.append('status', employeeData.status)
    if (employeeData.area !== undefined && employeeData.area !== null) formData.append('area', employeeData.area)

    if (employeeData.user) {
      if (employeeData.user.email) {
        formData.append('user.email', employeeData.user.email)
        formData.append('user.username', employeeData.user.email)
      }
      if (employeeData.user.first_name) formData.append('user.first_name', employeeData.user.first_name)
      if (employeeData.user.last_name) formData.append('user.last_name', employeeData.user.last_name)
      if (employeeData.user.cellNumber) formData.append('user.cellNumber', employeeData.user.cellNumber)
      if (employeeData.user.password) formData.append('user.password', employeeData.user.password)
      if (employeeData.user.idFront) formData.append('user.idFront', employeeData.user.idFront)
      if (employeeData.user.idBack) formData.append('user.idBack', employeeData.user.idBack)
      if (employeeData.user.avatar) formData.append('user.avatar', employeeData.user.avatar)
    }

    try {
      const response = await axios.patch(url, formData, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error updating employee:', error)
      throw error
    }
  },

  /**
   * Elimina un empleado
   * @param {string} employeeId - ID del empleado
   * @returns {Promise}
   */
  async deleteEmployee (employeeId) {
    const url = `${API_BASE}/employee/${employeeId}/`

    try {
      await axios.delete(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return true
    } catch (error) {
      console.error('Error deleting employee:', error)
      throw error
    }
  },

  /**
   * Asigna un permiso a un empleado
   * @param {string} employeeId - ID del empleado
   * @param {string} permission - Código de permiso (invitations, residents, employees, areas, fees)
   * @returns {Promise}
   */
  async grantPermission (employeeId, permission) {
    const url = `${API_BASE}/employee/${employeeId}/grant-permission/`

    try {
      const response = await axios.post(url, { permission }, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error granting permission:', error)
      throw error
    }
  },

  /**
   * Revoca un permiso de un empleado
   * @param {string} employeeId - ID del empleado
   * @param {string} permission - Código de permiso
   * @returns {Promise}
   */
  async revokePermission (employeeId, permission) {
    const url = `${API_BASE}/employee/${employeeId}/revoke-permission/`

    try {
      await axios.post(url, { permission }, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return true
    } catch (error) {
      console.error('Error revoking permission:', error)
      throw error
    }
  },

  // === EMPLOYEE SCHEDULES ===

  /**
   * Obtiene horarios de empleados por residencial
   * @param {string} housingUnitId - ID del residencial
   * @returns {Promise} Lista de horarios
   */
  async getEmployeeSchedules (housingUnitId) {
    const url = `${API_BASE}/employee-schedule/?housing_unit=${housingUnitId}`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching schedules:', error)
      throw error
    }
  },

  /**
   * Obtiene horario específico de un empleado
   * @param {string} scheduleId - ID del horario
   * @returns {Promise} Detalles del horario
   */
  async getEmployeeSchedule (scheduleId) {
    const url = `${API_BASE}/employee-schedule/${scheduleId}/`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching schedule:', error)
      throw error
    }
  },

  /**
   * Crea un nuevo horario para un empleado
   * @param {Object} scheduleData - Datos {employee, entry_time, exit_time, days_of_week}
   * @returns {Promise} Horario creado
   */
  async createEmployeeSchedule (scheduleData) {
    const url = `${API_BASE}/employee-schedule/`

    try {
      const response = await axios.post(url, scheduleData, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error creating schedule:', error)
      throw error
    }
  },

  /**
   * Actualiza un horario de empleado
   * @param {string} scheduleId - ID del horario
   * @param {Object} scheduleData - Datos a actualizar
   * @returns {Promise} Horario actualizado
   */
  async updateEmployeeSchedule (scheduleId, scheduleData) {
    const url = `${API_BASE}/employee-schedule/${scheduleId}/`

    try {
      const response = await axios.patch(url, scheduleData, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error updating schedule:', error)
      throw error
    }
  },

  /**
   * Elimina un horario de empleado
   * @param {string} scheduleId - ID del horario
   * @returns {Promise}
   */
  async deleteEmployeeSchedule (scheduleId) {
    const url = `${API_BASE}/employee-schedule/${scheduleId}/`

    try {
      await axios.delete(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return true
    } catch (error) {
      console.error('Error deleting schedule:', error)
      throw error
    }
  },

  // === EMPLOYEE ACCESS LOGS ===

  /**
   * Obtiene registros de acceso de un empleado
   * @param {string} employeeId - ID del empleado
   * @param {Object} filters - Filtros opcionales {from_date, to_date, page_size}
   * @returns {Promise} Lista de registros de acceso
   */
  async getEmployeeAccessLogs (employeeId, filters = {}) {
    let url = `${API_BASE}/employee-access/?employee=${employeeId}`

    if (filters.from_date) url += `&from_date=${filters.from_date}`
    if (filters.to_date) url += `&to_date=${filters.to_date}`
    if (filters.page_size) url += `&page_size=${filters.page_size}`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching access logs:', error)
      throw error
    }
  },

  /**
   * Obtiene un registro de acceso específico
   * @param {string} accessLogId - ID del registro
   * @returns {Promise} Detalles del registro
   */
  async getEmployeeAccessLog (accessLogId) {
    const url = `${API_BASE}/employee-access/${accessLogId}/`

    try {
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Token ${store.getters.getToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching access log:', error)
      throw error
    }
  }
}
